import React, { useState, useEffect, useRef } from "react";
import {
  getYouTubeThumbnailUrl,
  buildOptimizedYouTubeUrl,
  videoPerformanceMonitor,
  getOptimalVideoSettings,
} from "../utils/video-optimization";

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
  className?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  lazyLoad?: boolean;
  showThumbnail?: boolean;
  autoplay?: boolean;
  muted?: boolean;
}

// Helper function to extract video ID from various YouTube URL formats
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Handle youtu.be format
  if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1]?.split(/[?&#]/)[0];
    return id || null;
  }

  // Handle youtube.com/watch?v= format
  const watchMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/\?v=)([^?&#]+)/,
  );
  if (watchMatch) {
    return watchMatch[1] || null;
  }

  // If the input is already just a video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  return null;
}

// Get optimal video settings based on connection
const optimalSettings = getOptimalVideoSettings();

export default function YouTubeEmbed({
  videoId,
  title = "YouTube video player",
  className = "",
  aspectRatio = "16:9",
  lazyLoad = true,
  showThumbnail = true,
  autoplay = false,
  muted = false,
}: YouTubeEmbedProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazyLoad);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Ensure we have a clean video ID
  const cleanVideoId = extractYouTubeVideoId(videoId) || videoId;

  // Calculate padding based on aspect ratio
  const getPaddingBottom = () => {
    switch (aspectRatio) {
      case "4:3":
        return "75%";
      case "1:1":
        return "100%";
      case "16:9":
      default:
        return "56.25%";
    }
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazyLoad || !containerRef.current) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "50px", // Start loading 50px before the video comes into view
        threshold: 0.1,
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoad]);

  // Preload thumbnail when component becomes visible
  useEffect(() => {
    if (isVisible && showThumbnail && cleanVideoId) {
      const img = new Image();
      const startTime = performance.now();

      img.onload = () => {
        const loadTime = performance.now() - startTime;
        videoPerformanceMonitor.recordThumbnailLoad(cleanVideoId, loadTime);
        setThumbnailLoaded(true);
      };

      img.onerror = () => setThumbnailError(true);
      img.src = getYouTubeThumbnailUrl(cleanVideoId, optimalSettings.quality);
    }
  }, [isVisible, showThumbnail, cleanVideoId]);

  // Build YouTube embed URL with optimizations
  const buildEmbedUrl = () => {
    return buildOptimizedYouTubeUrl(cleanVideoId, {
      autoplay,
      muted,
      modestbranding: true,
      rel: false,
      showinfo: false,
      iv_load_policy: 3,
      cc_load_policy: 0,
    });
  };

  const handleIframeLoad = () => {
    const loadTime = performance.now();
    videoPerformanceMonitor.recordIframeLoad(cleanVideoId, loadTime);
    setIsLoaded(true);
  };

  // Start performance monitoring when component mounts
  useEffect(() => {
    if (containerRef.current) {
      videoPerformanceMonitor.startMonitoring(
        cleanVideoId,
        containerRef.current,
        {
          trackViewport: true,
          trackInteraction: true,
        },
      );
    }
  }, [cleanVideoId]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
      style={{ paddingBottom: getPaddingBottom() }}
    >
      {/* Thumbnail placeholder */}
      {showThumbnail && isVisible && !isLoaded && (
        <div className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md overflow-hidden bg-gray-100">
          {thumbnailLoaded && !thumbnailError ? (
            <img
              src={getYouTubeThumbnailUrl(
                cleanVideoId,
                optimalSettings.quality,
              )}
              alt={title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto mb-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
                <p className="text-sm">Loading video...</p>
              </div>
            </div>
          )}

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all duration-200 cursor-pointer">
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* YouTube iframe */}
      {isVisible && (
        <iframe
          ref={iframeRef}
          className={`absolute top-0 left-0 w-full h-full rounded-lg shadow-md transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          src={buildEmbedUrl()}
          title={title}
          loading={lazyLoad ? "lazy" : "eager"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={handleIframeLoad}
        />
      )}

      {/* Loading indicator */}
      {isVisible && !isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}
    </div>
  );
}
